/**
 * Aggregate registry of all Help articles. Add new articles by importing
 * them here — the HelpHome + Article pages read from this list.
 *
 * Naming: file slug should match article.slug so authoring stays
 * trivial: copy any starter file, rename, edit.
 */
import { aiFeaturesAndActivation } from './ai-features-and-activation';
import { automatingWithRules } from './automating-with-rules';
import { backdatingTickets } from './backdating-tickets';
import { bundledScheduledReports } from './bundled-scheduled-reports';
import { excelReportAttachment } from './excel-report-attachment';
import { groupedReportRendering } from './grouped-report-rendering';
import { matrixReportsWithTotals } from './matrix-reports-with-totals';
import { threeDimGroupedReports } from './three-dim-grouped-reports';
import { countryAndEngineersAndDailyPresets } from './country-and-engineers-and-daily-presets';
import { dailyReportPolish } from './daily-report-polish';
import { duplicateResolutionCategories202606 } from './duplicate-resolution-categories-2026-06';
import { ticketsListDateFilter } from './tickets-list-date-filter';
import { capturingAFieldVisit } from './capturing-a-field-visit';
import { csatPasswordlessFeedback } from './csat-passwordless-feedback';
import { cloningATicket } from './cloning-a-ticket';
import { customerAcknowledgement } from './customer-acknowledgement';
import { dispatchBoardAndWorkOrders } from './dispatch-board-and-work-orders';
import { dispatchPlaybook } from './dispatch-playbook';
import { editingATicket } from './editing-a-ticket';
import { emailBranding } from './email-branding';
import { emailSafetyPipeline } from './email-safety-pipeline';
import { emailTemplates } from './email-templates';
import { escalationsAndOnCall } from './escalations-and-on-call';
import { extensionSessions } from './extension-sessions';
import { fieldActivitiesReport } from './field-activities-report';
import { fieldServiceVisitReport } from './field-service-visit-report';
import { fieldServiceVisitWorkflow } from './field-service-visit-workflow';
import { findingTickets } from './finding-tickets';
import { formValidationRules } from './form-validation-rules';
import { multiVendorEngineerSourcing } from './multi-vendor-engineer-sourcing';
import { gettingStartedAdmin } from './getting-started-admin';
import { gettingStartedCustomer } from './getting-started-customer';
import { gettingStartedEngineer } from './getting-started-engineer';
import { gettingStartedFieldEngineer } from './getting-started-field-engineer';
import { loggingPreScreeningCalls } from './logging-pre-screening-calls';
import { loggingTimeAndTimesheets } from './logging-time-and-timesheets';
import { managingAssets } from './managing-assets';
import { assetCatalog } from './asset-catalog';
import { cmdbAndSam } from './cmdb-and-sam';
import { connectingIntuneDiscovery } from './connecting-intune-discovery';
import { monitoringAiops } from './monitoring-aiops';
import { walkthroughAlertToIncident } from './walkthrough-alert-to-incident';
import { managingRolesAndPermissions } from './managing-roles-and-permissions';
import { managingTheServiceCatalog } from './managing-the-service-catalog';
import { managingYourNotifications } from './managing-your-notifications';
import { notificationLog } from './notification-log';
import { projectContacts } from './project-contacts';
import { reportsAndExports } from './reports-and-exports';
import { scheduleAndDispatch } from './schedule-and-dispatch';
import { schedulingVisitsAndPocContact } from './scheduling-visits-and-poc-contact';
import { slaPolicyScope } from './sla-policy-scope';
import { ticketFlowEndToEndDiagram } from './ticket-flow-end-to-end-diagram';
import { ticketLifecycleAndLocks } from './ticket-lifecycle-and-locks';
import { timezoneDisplay } from './timezone-display';
import { reviewingCsatNpsCes } from './reviewing-csat-nps-ces';
import { understandingPriorityImpactUrgency } from './understanding-priority-impact-urgency';
import { suggestedEngineers } from './suggested-engineers';
import { teamChat } from './team-chat';
import { usingMacrosForOneClickActions } from './using-macros-for-one-click-actions';
import { vendorDispatchPlaybook } from './vendor-dispatch-playbook';
import { multiEngineerCrew } from './multi-engineer-crew';
import { pickingACrewMode } from './picking-a-crew-mode';
import { perEngineerTimeMode } from './per-engineer-time-mode';
import { flatCrewMode } from './flat-crew-mode';
import { rejectionReasonCategories } from './rejection-reason-categories';
import { freelancerDispatch } from './freelancer-dispatch';
import { vendorEngineerDelegation } from './vendor-engineer-delegation';
import { vendorMasterAndScorecards } from './vendor-master-and-scorecards';
import { vendorPortal } from './vendor-portal';
import { workingATicketEndToEnd } from './working-a-ticket-end-to-end';
export const HELP_ARTICLES = [
    gettingStartedAdmin,
    gettingStartedEngineer, // body now references "Service Desk Agent"
    gettingStartedFieldEngineer, // new 2026-05 article for the FSM role
    gettingStartedCustomer,
    dispatchPlaybook, // core-business onsite runbook; leads field-service
    ticketFlowEndToEndDiagram, // picture-first map; leads the Tickets section
    workingATicketEndToEnd,
    findingTickets, // Tickets list filters (incl. Created-date)
    loggingPreScreeningCalls, // record the pre-dispatch customer call to the timeline
    editingATicket, // full Edit form — sections + the 9 locked fields
    ticketLifecycleAndLocks,
    cloningATicket, // create a new ticket pre-filled from an existing one
    projectContacts, // project requester roster (flag: phase2_projects)
    schedulingVisitsAndPocContact,
    capturingAFieldVisit,
    customerAcknowledgement, // ADR-018 — email OTP / link customer approval
    suggestedEngineers,
    teamChat,
    usingMacrosForOneClickActions,
    formValidationRules,
    emailSafetyPipeline,
    escalationsAndOnCall,
    automatingWithRules, // §8.6 — rules engine + auto-routing (2026-06)
    backdatingTickets, // ADR-021 — log past events with proper SLA + audit
    fieldActivitiesReport, // ticket-grain daily activity run sheet (2026-07-16)
    fieldServiceVisitReport, // per-visit detail list report (§8.1)
    fieldServiceVisitWorkflow,
    dispatchBoardAndWorkOrders,
    vendorDispatchPlaybook, // §22.14 — subcontracted onsite job, no-login vendor link
    multiVendorEngineerSourcing, // competitive parallel sourcing (flag: multi_vendor_sourcing)
    scheduleAndDispatch, // ADR-017 — capture the visit time before dispatch
    vendorEngineerDelegation, // ADR-015 — vendor hands the job to their field engineer
    freelancerDispatch, // 2026-07-23 — our own freelance engineers, dispatched directly
    vendorPortal, // M3 — logged-in vendor dashboard + company job queue
    pickingACrewMode, // 4-mode decision tree — Solo / Lead / Per-engineer / Flat (flag: crew_assignment)
    multiEngineerCrew, // ADR-019 — 1 Lead + up to 4 Members on one WO (flag: crew_assignment)
    perEngineerTimeMode, // Mode C — Lead-driven + per-Member billable hours (flag: crew_mode_per_engineer_time)
    flatCrewMode, // Mode D — no Lead, everyone equal, spokesperson signs (flag: crew_mode_flat_no_lead)
    rejectionReasonCategories, // 5-option SDA reject dropdown — Position closed = no repropose (flag: crew_assignment)
    extensionSessions, // 0108 — extra-work sessions added on site (flag: visit_extension_sessions)
    loggingTimeAndTimesheets,
    managingAssets, // §44 — Light CMDB device inventory
    assetCatalog, // §44 — Manufacturers & Models catalog
    cmdbAndSam, // §44.9 — P2 CMDB/SAM toolkit
    connectingIntuneDiscovery, // §44 — Intune→Discovery setup (read-only Graph pass + nightly sync)
    monitoringAiops, // §9.5 — monitoring/AIOps alert correlation
    walkthroughAlertToIncident, // end-to-end narrative: alert storm → incident → fix
    aiFeaturesAndActivation, // §14.23/§30.5 — AI features + value dashboard + activation
    managingRolesAndPermissions, // RBAC Insights / Role Permissions Matrix (full coverage)
    managingTheServiceCatalog,
    slaPolicyScope, // SLA scope precedence (end-client / client / service)
    managingYourNotifications, // self-service notification preferences (opt-out grid)
    emailBranding, // tenant email branding (logo / footer / accent)
    emailTemplates, // per-event email wording overrides
    notificationLog,
    vendorMasterAndScorecards,
    reportsAndExports, // dashboards + custom builder + CSV/Excel/PDF exports
    bundledScheduledReports, // Path B Commit 1 — N saved reports per scheduled email
    groupedReportRendering, // Path B Commit 2 — Excel-style subtotals + grand total chart type
    excelReportAttachment, // Path B Commit 3 — .xlsx download link on scheduled reports
    matrixReportsWithTotals, // Path B Commit B — Excel pivot-table chart + calendar-period filter
    threeDimGroupedReports, // Path B Commit C — 3-dim grouped + Visits measure
    countryAndEngineersAndDailyPresets, // Step 2 — Country dim + Yesterday/Today + Engineer(s) text measure
    dailyReportPolish, // Step 3 — Country filter + date_field + 4-dim grouped
    duplicateResolutionCategories202606, // 2026-06-24 — duplicate-title warning + 6 resolution codes + AP categories
    ticketsListDateFilter, // 2026-06-29 — list filter by Created / Activity / Visit scheduled
    reviewingCsatNpsCes,
    csatPasswordlessFeedback, // no-login /csat/<token> rating page + agent alerts
    understandingPriorityImpactUrgency,
    timezoneDisplay, // explicit TZ labels on every displayed time
];
export function findArticle(slug) {
    return HELP_ARTICLES.find((a) => a.slug === slug);
}
